import { useEffect, useRef, useState } from 'react'
import { Download, MoreHorizontal, Share, SquarePlus, ChevronDown } from 'lucide-react'
import { Sheet } from './UI'
import { safeStorage } from '../lib/safeStorage'
import { logEvent } from '../lib/analytics'
import { isOverlayBusy } from '../lib/overlayBusy'
import { noteMark, claimAsk } from '../lib/askQueue'

// ── InstallAsk — the Home Screen step, done right per platform ────────────────
// 2026-10-15 (Jason: "the iOS install step, and make sure Android is solid").
// In Safari there is no push and storage dies after 7 days away; on the Home
// Screen both problems vanish. The old banner said "tap the Share icon", but on
// iOS 26 Share lives under the "…" button, then View More, then Add to Home
// Screen: five taps, three hidden. This sheet shows the real steps. On Android the
// install event fires once, early at load; main.jsx now captures it, so the
// button here is a real one-tap install. Fires once per device after the first
// mark, ahead of the push ask (which needs the install on iOS anyway).
const KEY = 'nextep_install_ask'

export const isStandalone = () => { try { return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true } catch { return false } }
export const platform = () => {
  const ua = navigator.userAgent || ''
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (ios) return /CriOS|FxiOS|EdgiOS/.test(ua) ? 'ios-other' : 'ios-safari'
  if (/Android/.test(ua)) return 'android'
  return 'desktop'
}

export function InstallAsk({ user, delay = 3000 }) {
  const [open, setOpen] = useState(false)
  const [evt, setEvt] = useState(() => window.__nextepInstallEvt || null)
  const [done, setDone] = useState(false)
  const timer = useRef(null)
  const plat = platform()

  useEffect(() => {
    const onInstallable = () => setEvt(window.__nextepInstallEvt || null)
    const onInstalled = () => { setDone(true); logEvent('install', { result: 'installed', plat }) }
    window.addEventListener('nextep:installable', onInstallable)
    window.addEventListener('appinstalled', onInstalled)
    return () => { window.removeEventListener('nextep:installable', onInstallable); window.removeEventListener('appinstalled', onInstalled) }
  }, [plat])

  // Opens on demand (Profile row) regardless of the once-guard.
  useEffect(() => {
    const onOpen = () => { if (isStandalone()) return; logEvent('install_ask', { result: 'shown', src: 'row', plat }); setOpen(true) }
    window.addEventListener('nextep:open-install', onOpen)
    return () => window.removeEventListener('nextep:open-install', onOpen)
  }, [plat])

  useEffect(() => {
    if (!user?.id || isStandalone()) return
    if (plat === 'desktop') return
    const onMark = (e) => {
      const markId = noteMark(e); if (!markId) return
      if (safeStorage.get(KEY)) return
      if (timer.current) return
      const tryOpen = (left) => {
        if (isOverlayBusy() && left > 0) { timer.current = setTimeout(() => tryOpen(left - 1), 700); return }
        timer.current = null
        if (isOverlayBusy()) return
        if (!claimAsk(markId)) return   // another ask took this mark; keep our guard for the next one
        safeStorage.set(KEY, new Date().toISOString())
        logEvent('install_ask', { result: 'shown', src: 'mark', plat, oneTap: !!(window.__nextepInstallEvt) })
        setOpen(true)
      }
      timer.current = setTimeout(() => tryOpen(12), delay)
    }
    window.addEventListener('nextep:progress-marked', onMark)
    return () => { window.removeEventListener('nextep:progress-marked', onMark); if (timer.current) clearTimeout(timer.current); timer.current = null }
  }, [user?.id, plat, delay])

  useEffect(() => {
    if (!open) return
    window.__nextepModalHold = true
    return () => { window.__nextepModalHold = false }
  }, [open])

  if (!open) return null
  const close = (result) => { setOpen(false); if (result) logEvent('install_ask', { result, plat }) }
  const installNow = async () => {
    const e = evt || window.__nextepInstallEvt
    if (!e) return
    try {
      e.prompt()
      const { outcome } = await e.userChoice
      logEvent('install_ask', { result: outcome === 'accepted' ? 'accepted' : 'declined', plat })
      window.__nextepInstallEvt = null; setEvt(null)
      if (outcome === 'accepted') { setDone(true); setTimeout(() => setOpen(false), 1800) }
    } catch { logEvent('install_ask', { result: 'prompt_failed', plat }) }
  }

  const Step = ({ n, icon, text }) => (
    <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ width: 24, height: 24, borderRadius: 'var(--radius-full)', flexShrink: 0, background: 'rgba(var(--amber-rgb),0.14)', color: 'var(--amber)', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
      <span style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-strong)' }}>{icon}</span>
      <span style={{ color: 'var(--text)', fontSize: 14.5, lineHeight: 1.35 }}>{text}</span>
    </li>
  )
  const ghost = { flex: 1, height: 48, borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: 'var(--font)' }
  const primary = { flex: 1.3, height: 48, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'var(--font)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }
  const oneTap = plat === 'android' && !!(evt || window.__nextepInstallEvt)

  return (
    <Sheet open onClose={() => close('dismissed')} label="Put NextEp on your Home Screen" from="bottom">
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, margin: '0 auto 8px', background: 'var(--amber)', color: 'var(--amber-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 22px rgba(var(--amber-rgb),0.3)' }}><Download size={20} /></div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--amber)' }}>{done ? 'Installed' : 'One more step'}</div>
        <div style={{ color: 'var(--text-strong)', fontFamily: 'var(--font-serif)', fontSize: 21, lineHeight: 1.15, marginTop: 4 }}>{done ? 'NextEp is on your Home Screen.' : 'Put NextEp on your Home Screen'}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 13.5, marginTop: 4, lineHeight: 1.45 }}>
          {done ? 'Open it from there. Reminders and your list now live on this phone for good.'
            : plat === 'ios-safari' ? 'Then it opens like an app, reminders can reach you, and your list stays put.'
            : plat === 'ios-other' ? 'On iPhone this works from Safari. Open app.nextep.show there, then follow these steps.'
            : 'It opens like an app, reminders can reach you, and your list stays put.'}
        </div>
      </div>
      {!done && (plat === 'ios-safari' || plat === 'ios-other') && (
        <ol style={{ listStyle: 'none', margin: '0 0 12px', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Step n="1" icon={<MoreHorizontal size={18} />} text="Tap the … button at the bottom of Safari" />
          <Step n="2" icon={<Share size={17} />} text="Tap Share" />
          <Step n="3" icon={<ChevronDown size={18} />} text="Tap View More" />
          <Step n="4" icon={<SquarePlus size={18} />} text="Tap Add to Home Screen, then Add" />
        </ol>
      )}
      {!done && plat === 'android' && !oneTap && (
        <ol style={{ listStyle: 'none', margin: '0 0 12px', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Step n="1" icon={<MoreHorizontal size={18} style={{ transform: 'rotate(90deg)' }} />} text="Tap the ⋮ menu at the top of Chrome" />
          <Step n="2" icon={<SquarePlus size={18} />} text="Tap Add to Home screen, then Install" />
        </ol>
      )}
      {!done && (
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => close('dismissed')} style={ghost}>Not now</button>
          {oneTap ? <button onClick={installNow} style={primary}><Download size={17} /> Install NextEp</button>
                  : <button onClick={() => close('got_it')} style={primary}>Got it</button>}
        </div>
      )}
    </Sheet>
  )
}
